import { apiClient } from '../utils/api-client';
import type {
  ClimbScan,
  ClimbScanColor,
  SaveColorDraftData,
  SaveScanClimbData,
  PublishDraftData,
  RecentClimbScan,
  ScanDetector,
  ScanSensitivity,
  DetectedHold,
  PhotoArea,
} from '../types/climb-scan';
import type { Climb } from '../types/climb';

export class ScanValidationError extends Error {
  fieldErrors: Record<string, string>;
  constructor(fieldErrors: Record<string, string>) {
    super('Validation failed');
    this.fieldErrors = fieldErrors;
    this.name = 'ScanValidationError';
  }
}

type JsonApiError = { detail?: string; status?: string; source?: { pointer?: string } };

export async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let rawErrors: JsonApiError[] = [];
    try {
      const err = await response.json() as { errors?: JsonApiError[] };
      rawErrors = err.errors ?? [];
    } catch {
      // ignore parse failure
    }
    if (response.status === 422 && rawErrors.length > 0) {
      const fieldErrors: Record<string, string> = {};
      for (const e of rawErrors) {
        const field = e.source?.pointer?.split('/').pop() ?? 'base';
        fieldErrors[field] = e.detail ?? 'Invalid';
      }
      throw new ScanValidationError(fieldErrors);
    }
    throw new Error(rawErrors[0]?.detail ?? `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

class ClimbScansService {
  async getScans(gymSlug: string): Promise<ClimbScan[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/climb_scans`);
    const json = await parseJson<{ data: ClimbScan[] }>(response);
    return json.data;
  }

  async getScan(gymSlug: string, scanId: string): Promise<ClimbScan> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/climb_scans/${scanId}`);
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * The walls this place has had scanned, newest first, for the climb editor to
   * offer beside the camera. Without their holds — those are read with
   * `getScan` once one is picked — which, to a member, answers with the wall
   * alone. Every scan to the place's climb staff; to anybody else only the
   * walls with a climb up on them.
   *
   * `liveScan` says whether a photo taken now, with the camera or from the
   * gallery, would be read for holds on the spot (`startLiveScan`).
   */
  async getRecentScans(
    locationId: string | number,
    type: 'boulder' | 'route'
  ): Promise<{ scans: RecentClimbScan[]; liveScan: boolean }> {
    const response = await apiClient.get(
      `/api/locations/${locationId}/recent_climb_scans?type=${type}`,
      { requireAuth: true }
    );
    const json = await parseJson<{ data: RecentClimbScan[]; meta?: { live_scan?: boolean } }>(response);
    return { scans: json.data ?? [], liveScan: !!json.meta?.live_scan };
  }

  /**
   * A photo taken while a climb is put up, sent to be read for holds there
   * and then (`ClimbScans::LiveScan`). The scan comes back `pending`; read it
   * with `getScan` until it is `ready` or `failed` — its `progress` says how
   * far it has got meanwhile.
   */
  async startLiveScan(
    gymSlug: string,
    data: { image_signed_id: string; climb_type: 'boulder' | 'route' }
  ): Promise<ClimbScan> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/climb_scans/live`, {
      data: { attributes: data },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  async createScan(gymSlug: string, data: { climb_type: string; image_signed_id: string }): Promise<ClimbScan> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/climb_scans`,
      { data: { attributes: data } }
    );
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  async updateScan(
    gymSlug: string,
    scanId: string,
    data: {
      zone_id?: string | null;
      route_setter_ids?: string[];
      min_score?: number | null;
      excluded_holds?: number[];
    }
  ): Promise<ClimbScan> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}`,
      { data: { attributes: data } }
    );
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * A hold a setter outlined by hand (the panel's colour-wand), added to the
   * wall: `outline` on the 0-1000 grid. The server measures its colour and
   * finds its group; the scan comes back with it at the end of
   * `detected_holds`.
   */
  async addHold(gymSlug: string, scanId: string, outline: Array<[number, number]>): Promise<ClimbScan> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/climb_scans/${scanId}/holds`, {
      data: { attributes: { outline } },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * Look at this wall. The first look, the retry of an analysis that failed and
   * the re-scan of one that came back wrong are the same request — uploading a
   * photo starts nothing, so a wall is looked at because this was pressed or
   * because the hourly batch came round.
   *
   * `reason` says what was wrong with the last answer, and is the only thing
   * the model is told about it: the boxes it produced are deliberately not fed
   * back, since a wrong answer handed back is signal it anchors on rather than
   * learns from. A first look has nothing to describe, so it goes without one.
   *
   * `wholePhoto` asks for one more look, at the whole photo, for the big holds
   * and volumes the tiles cut. A re-scan's to ask for: the server ignores it on
   * a wall that has never answered.
   *
   * `detector` names who answers instead of the gym's default — `'sam'` for
   * the precise look (`ClimbScan.precise_scan`). Null is the default; a
   * detector the gym does not have is ignored by the server.
   */
  async startScan(
    gymSlug: string,
    scanId: string,
    reason?: string | null,
    sensitivity?: ScanSensitivity | null,
    wholePhoto = false,
    detector: ScanDetector | null = null
  ): Promise<ClimbScan> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/climb_scans/${scanId}/scan`, {
      data: {
        attributes: {
          reason: reason || null,
          sensitivity: sensitivity || null,
          whole_photo: wholePhoto,
          detector,
        },
      },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * Look again at one area of the wall, for holds the scan missed. The answer
   * lands later: the scan comes back with `area_scan.running`, and reading it
   * again says when it is done and how many holds it added.
   */
  async scanArea(gymSlug: string, scanId: string, area: PhotoArea): Promise<ClimbScan> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/climb_scans/${scanId}/scan_area`, {
      data: { attributes: { area } },
    });
    const json = await parseJson<{ data: ClimbScan }>(response);
    return json.data;
  }

  /**
   * Look at one colour again rather than the whole wall. The colour's name and
   * hex go with it, so the model is asked to find something it has been
   * described instead of working the wall out from scratch.
   */
  async rescanColor(
    gymSlug: string,
    scanId: string,
    colorId: string,
    reason?: string | null
  ): Promise<ClimbScanColor> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors/${colorId}/rescan`,
      { data: { attributes: { reason: reason || null } } }
    );
    const json = await parseJson<{ data: ClimbScanColor }>(response);
    return json.data;
  }

  async getColor(gymSlug: string, colorId: string): Promise<ClimbScanColor> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/climb_scan_colors/${colorId}`);
    const json = await parseJson<{ data: ClimbScanColor }>(response);
    return json.data;
  }

  async saveDraftColor(
    gymSlug: string,
    scanId: string,
    colorId: string,
    data: SaveColorDraftData
  ): Promise<ClimbScanColor> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors/${colorId}/save_draft`,
      { data: { attributes: data } }
    );
    const json = await parseJson<{ data: ClimbScanColor }>(response);
    return json.data;
  }

  async createColor(gymSlug: string, scanId: string, colorName?: string): Promise<ClimbScanColor> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors`,
      { data: { attributes: { color_name: colorName ?? null } } }
    );
    const json = await parseJson<{ data: ClimbScanColor }>(response);
    return json.data;
  }

  async deleteColor(gymSlug: string, scanId: string, colorId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors/${colorId}`
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  }

  async updateColorName(gymSlug: string, scanId: string, colorId: string, colorName: string): Promise<ClimbScanColor> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors/${colorId}`,
      { data: { attributes: { color_name: colorName } } }
    );
    const json = await parseJson<{ data: ClimbScanColor }>(response);
    return json.data;
  }

  /**
   * Turn one of a scan's colours into a confirmed climb, marks and all.
   *
   * Two calls because they do two different things and only the first one can
   * do it: `save_draft` is what creates the climb and links it to the colour —
   * attaching the scan photo as the climb's own image, which is what the holds
   * and the line are drawn against — while `publish` is what writes those marks
   * and confirms it. Running both here keeps that pairing in one place instead
   * of leaving every caller to remember the order.
   */
  async saveScanClimb(
    gymSlug: string,
    scanId: string,
    colorId: string,
    data: SaveScanClimbData
  ): Promise<ClimbScanColor> {
    const color = await this.saveDraftColor(gymSlug, scanId, colorId, {
      name: data.name,
      grade_id: data.grade_id,
      route_setter_id: data.route_setter_id,
      zone_id: data.zone_id,
      color_name: data.color_name,
    });

    if (!color.climb) throw new Error('Draft climb was not created');

    await this.publishDraftClimb(color.climb.id, {
      name: data.name,
      grade_id: data.grade_id,
      zone_id: data.zone_id,
      route_setter_id: data.route_setter_id,
      hold_color: data.color_name ?? undefined,
      description: data.description,
      climb_exceptions: data.climb_exceptions,
      holds: data.holds,
      line: data.line,
      // A circuit is climbed in the order its holds arrive in, and a forced one
      // says which hand takes each of them — see photo-circuit.ts.
      circuit: data.circuit,
      forced_hands: data.forced_hands,
      // A crop, where the setter framed the climb on the way up. The wall it was
      // cut out of is kept as the climb's original by the endpoint itself — it
      // is the picture the draft is already holding, so it need not go up twice.
      ...(data.image_signed_id
        ? { image_signed_id: data.image_signed_id, cropped: true }
        : {}),
    });

    // Publishing moves the colour to `completed` and rewrites the climb, so the
    // record the first call returned is already stale.
    return this.getColor(gymSlug, colorId);
  }

  /**
   * Make a climb out of what was chosen on a scanned wall.
   *
   * **One call, because nothing exists until this one.** The old path walked
   * three — create the colour, save a draft against it, publish that draft —
   * which meant a scan left half-made climbs in the database whether anybody
   * finished them or not. A scan now proposes without writing anything down,
   * and this is the single moment it becomes real.
   */
  async publishScanClimb(
    gymSlug: string,
    scanId: string,
    data: SaveScanClimbData & { detected_holds?: DetectedHold[] }
  ): Promise<ClimbScanColor> {
    const { colorId } = await this.publishOffScan(gymSlug, scanId, data);

    return this.getColor(gymSlug, colorId);
  }

  /**
   * The same climb, made off a scanned wall from the member app's editor — which
   * wants the climb back rather than the scan's row, because what it opens next
   * is the dialog of everything asked once a climb is up.
   *
   * Through the scan rather than as a climb of its own, because that is what it
   * is: a choice of the holds the scan found, which the panel then lists among
   * the climbs on that wall instead of offering them again as untouched.
   */
  async publishClimbOnScan(
    gymSlug: string,
    scanId: string,
    data: SaveScanClimbData & { detected_holds?: DetectedHold[] }
  ): Promise<Climb> {
    const { climb } = await this.publishOffScan(gymSlug, scanId, data);

    return climb;
  }

  /** The row and its draft in one call, then the ordinary publish of that draft. */
  private async publishOffScan(
    gymSlug: string,
    scanId: string,
    data: SaveScanClimbData & { detected_holds?: DetectedHold[] }
  ): Promise<{ colorId: string; climb: Climb }> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/climb_scans/${scanId}/colors/publish`,
      {
        data: {
          attributes: {
            // The draft is born with what identifies it, so its slug is built
            // from the name rather than from nothing.
            name: data.name,
            grade_id: data.grade_id,
            zone_id: data.zone_id ?? null,
            route_setter_id: data.route_setter_id ?? null,
            color_name: data.color_name ?? null,
            holds: data.holds ?? [],
            detected_holds: data.detected_holds ?? [],
          },
        },
      }
    );
    const json = await parseJson<{ data: ClimbScanColor; meta: { climb_id: string } }>(response);

    // The row exists now; everything the form says about the climb goes through
    // the ordinary publish, which is the one place those rules live.
    const climb = await this.publishDraftClimb(json.meta.climb_id, {
      name: data.name,
      grade_id: data.grade_id,
      zone_id: data.zone_id,
      route_setter_id: data.route_setter_id,
      hold_color: data.color_name ?? undefined,
      description: data.description,
      climb_exceptions: data.climb_exceptions,
      holds: data.holds,
      line: data.line,
      circuit: data.circuit,
      forced_hands: data.forced_hands,
      ...(data.image_signed_id
        ? { image_signed_id: data.image_signed_id, cropped: true }
        : {}),
    });

    return { colorId: json.data.id, climb };
  }

  async publishDraftClimb(climbId: string, data: PublishDraftData): Promise<Climb> {
    const response = await apiClient.post(
      `/api/climbs/${climbId}/publish`,
      { data: { attributes: data } }
    );
    const json = await parseJson<{ data: { attributes: Climb } }>(response);
    return json.data.attributes;
  }
}

export const climbScansService = new ClimbScansService();
export type { PublishDraftData };
